// PDP Recharge subscription widget — copy, layout hooks, and toggle behavior.
// Recharge's <recharge-subscription-widget> uses an open shadow root; this script
// adds ::part() names custom.css needs and keeps the sub-panel visible when
// one-time purchase is selected (Recharge removes that node from the DOM).
(function initPdpRechargeSubscriptionWidget() {
    try {
        const LEGACY_OTP_PANEL_ATTR = 'data-pdp-otp-panel';
        const SUB_PANEL_CLONE_ATTR = 'data-rc-sub-panel-clone';
        const PURCHASE_MODE_ATTR = 'data-recharge-purchase-mode';

        const BENEFITS = [
            'Free gift rewards',
            'Skip, reschedule or cancel anytime',
            '20% off after 3 months',
            'Consistency is key!'
        ];
        const FREQUENCY_LABEL = 'Delivered every';

        const addPart = (element, token) => {
            if (!element) return;

            const parts = (element.getAttribute('part') || '').split(/\s+/).filter(Boolean);
            if (parts.includes(token)) return;

            parts.push(token);
            element.setAttribute('part', parts.join(' '));
        };

        const setId = (element, id) => {
            if (element && element.id !== id) element.id = id;
        };

        const applyBenefits = (root) => {
            const list = root
                .querySelector('rc-benefits')
                ?.shadowRoot?.querySelector('.rc-benefits__list ul');

            if (!list) return;

            if (!list.dataset.rcEnhanceOriginal) {
                list.dataset.rcEnhanceOriginal = list.innerHTML;
            }

            const markup = BENEFITS.map((text) => `<li>${text}</li>`).join('');

            if (list.innerHTML !== markup) list.innerHTML = markup;
        };

        const positionFrequencyIcon = (root) => {
            const plans = root.querySelector('rc-selling-plans')?.shadowRoot;
            const icon = plans?.querySelector('.rc-plans-dropdown__icon');
            if (!icon) return;

            const box = icon.parentElement;
            if (box && box.style.position !== 'relative') box.style.position = 'relative';

            if (icon.dataset.rcEnhanceIconStyle === undefined) {
                icon.dataset.rcEnhanceIconStyle = icon.getAttribute('style') || '';
            }

            Object.assign(icon.style, {
                position: 'absolute',
                top: '50%',
                right: '14px',
                left: 'auto',
                transform: 'translateY(-50%)',
                width: '14px',
                height: '14px',
                color: '#121212',
                pointerEvents: 'none'
            });
        };

        const applyFrequencyOptions = (root) => {
            const options = root
                .querySelector('rc-selling-plans')
                ?.shadowRoot?.querySelectorAll('select option');

            options?.forEach((option) => {
                if (!option.dataset.rcEnhanceOriginal) {
                    option.dataset.rcEnhanceOriginal = option.textContent.trim();
                }

                const interval = option.dataset.rcEnhanceOriginal.split(':')[0].trim();
                const label = `${FREQUENCY_LABEL} ${interval}`;

                if (option.textContent !== label) option.textContent = label;
            });
        };

        const flattenForClone = (node) => {
            if (node.nodeType === Node.TEXT_NODE) return node.cloneNode(true);
            if (node.nodeType !== Node.ELEMENT_NODE) return null;

            const flat = node.shadowRoot ? document.createElement('div') : node.cloneNode(false);

            if (node.shadowRoot && node.hasAttribute('part')) {
                flat.setAttribute('part', node.getAttribute('part'));
            }

            const children = node.shadowRoot ? node.shadowRoot.childNodes : node.childNodes;
            children.forEach((child) => {
                const flatChild = flattenForClone(child);
                if (flatChild) flat.appendChild(flatChild);
            });

            if (flat.nodeType === Node.ELEMENT_NODE) flat.removeAttribute('id');

            if (flat.nodeType === Node.ELEMENT_NODE && 'disabled' in flat) flat.disabled = true;
            if (['SELECT', 'INPUT', 'BUTTON', 'A'].includes(flat.tagName)) {
                flat.setAttribute('tabindex', '-1');
                flat.removeAttribute('href');
            }

            return flat;
        };

        const styleClonedBenefits = (clone) => {
            const list = clone.querySelector('.rc-benefits__list ul');
            if (!list) return;

            list.style.margin = '0';
            list.style.padding = '0';
            list.style.listStyle = 'none';

            list.querySelectorAll('li').forEach((li) => {
                li.style.display = 'flex';
                li.style.alignItems = 'center';
                li.style.gap = '8px';
                li.style.margin = '0 0 9px';
                li.style.padding = '0';
                li.style.listStyle = 'none';
                li.style.color = '#9a9a9a';
                li.insertAdjacentHTML(
                    'afterbegin',
                    '<svg width="12" height="12" viewBox="0 0 24 24" style="flex:none;display:block" aria-hidden="true" focusable="false">' +
                        '<path d="M6 6 18 18M18 6 6 18" fill="none" stroke="#9a9a9a" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>'
                );
            });
        };

        const syncSubContainerClone = (subscription) => {
            if (!subscription) return;

            const live = subscription.querySelector(
                `.rc-purchase-option__sub-container:not([${SUB_PANEL_CLONE_ATTR}])`
            );
            const clone = subscription.querySelector(`[${SUB_PANEL_CLONE_ATTR}]`);

            if (live) {
                const flat = flattenForClone(live);
                flat.setAttribute(SUB_PANEL_CLONE_ATTR, 'true');
                styleClonedBenefits(flat);
                subscription.dataset.rcSubPanelSnapshot = flat.outerHTML;
                clone?.remove();
                return;
            }

            if (clone || !subscription.dataset.rcSubPanelSnapshot) return;

            subscription.insertAdjacentHTML('beforeend', subscription.dataset.rcSubPanelSnapshot);
        };

        const primedWidgets = new WeakSet();

        const primeSubContainerSnapshot = (widget, root, subscription) => {
            if (primedWidgets.has(widget)) return;
            if (subscription.querySelector('.rc-purchase-option__sub-container')) return;
            if (subscription.dataset.rcSubPanelSnapshot) return;

            const subscriptionInput = root.querySelector('input[value="subscription"]');
            const onetimeInput = root.querySelector('input[value="onetime"]');
            if (!subscriptionInput || !onetimeInput?.checked) return;

            primedWidgets.add(widget);

            const originalVisibility = widget.style.visibility;
            widget.style.visibility = 'hidden';

            const restore = () => {
                onetimeInput.click();
                requestAnimationFrame(() => requestAnimationFrame(() => {
                    widget.style.visibility = originalVisibility;
                }));
            };

            const observer = new MutationObserver(() => {
                if (!subscription.querySelector('.rc-purchase-option__sub-container')) return;
                observer.disconnect();
                positionFrequencyIcon(root);
                syncSubContainerClone(subscription);
                restore();
            });
            observer.observe(subscription, { childList: true, subtree: true });

            subscriptionInput.click();

            setTimeout(() => {
                observer.disconnect();
                widget.style.visibility = originalVisibility;
            }, 3000);
        };

        const cleanupLegacyInjections = (onetime) => {
            onetime?.querySelector(`[${LEGACY_OTP_PANEL_ATTR}]`)?.remove();
        };

        const applyWidget = (widget) => {
            const root = widget.shadowRoot;
            if (!root) return;

            const subscription = root.querySelector('.rc-purchase-option__subscription');
            const onetime = root.querySelector('.rc-selection__onetime');

            if (!subscription && !onetime) return;

            if (subscription) {
                addPart(subscription, 'rcx-sub-option');
                setId(subscription.querySelector('.rc-purchase-option__selector'), 'rcx-sub-selector');
                addPart(subscription.querySelector('.rc-purchase-option__label'), 'rcx-sub-label');
                addPart(subscription.querySelector('rc-radio-icon'), 'rcx-sub-indicator');
                addPart(subscription.querySelector('.rc-purchase-option__prices'), 'rcx-sub-prices');
                addPart(
                    subscription.querySelector('.rc-purchase-option__prices > .visually-hidden'),
                    'rcx-sub-price-sr'
                );
                addPart(subscription.querySelector('.rc-purchase-option__sub-container'), 'rcx-sub-panel');
                addPart(subscription.querySelector('rc-learn-more'), 'rcx-learn-more');
            }

            if (onetime) {
                addPart(onetime, 'rcx-otp-option');
                setId(onetime.querySelector('.rc-purchase-option__selector'), 'rcx-otp-selector');
                addPart(onetime.querySelector('.rc-purchase-option__label'), 'rcx-otp-label');
                addPart(onetime.querySelector('rc-radio-icon'), 'rcx-otp-indicator');
                addPart(onetime.querySelector('.rc-purchase-option__prices'), 'rcx-otp-prices');
            }

            const selected = subscription?.hasAttribute('rc-selected') ? 'subscription' : 'onetime';
            const modeValue = widget.getAttribute(PURCHASE_MODE_ATTR);
            if (modeValue !== selected) widget.setAttribute(PURCHASE_MODE_ATTR, selected);

            cleanupLegacyInjections(onetime);

            applyBenefits(root);
            applyFrequencyOptions(root);

            if (subscription) {
                positionFrequencyIcon(root);
                primeSubContainerSnapshot(widget, root, subscription);
                syncSubContainerClone(subscription);
            }
        };

        const swapToOnetime = (widget, event) => {
            const root = widget.shadowRoot;
            const selector = root.querySelector(
                '.rc-purchase-option__subscription .rc-purchase-option__selector'
            );

            if (!selector || !event.composedPath().includes(selector)) return;

            const subscription = root.querySelector('input[value="subscription"]');
            const onetime = root.querySelector('input[value="onetime"]');

            if (!subscription?.checked || !onetime) return;

            event.preventDefault();
            event.stopPropagation();
            onetime.click();
        };

        const bound = new WeakSet();

        const bindToggle = (widget) => {
            if (bound.has(widget)) return;

            bound.add(widget);

            widget.shadowRoot.addEventListener(
                'click',
                (event) => {
                    if (event.composedPath()[0] instanceof HTMLInputElement) return;
                    swapToOnetime(widget, event);
                },
                true
            );

            widget.shadowRoot.addEventListener(
                'keydown',
                (event) => {
                    if (event.key !== ' ' && event.key !== 'Enter') return;
                    swapToOnetime(widget, event);
                },
                true
            );
        };

        const observed = new WeakSet();

        const observe = (target) => {
            if (!target || observed.has(target)) return;

            observed.add(target);
            new MutationObserver(() => applyAll()).observe(target, {
                childList: true,
                subtree: true,
                characterData: true,
                attributes: true,
                attributeFilter: ['rc-selected', 'checked', 'selected', 'class']
            });
        };

        const applyAll = () => {
            document.querySelectorAll('recharge-subscription-widget').forEach((widget) => {
                if (!widget.shadowRoot) return;

                applyWidget(widget);
                bindToggle(widget);

                observe(widget.shadowRoot);
                observe(widget.shadowRoot.querySelector('rc-benefits')?.shadowRoot);
                observe(widget.shadowRoot.querySelector('rc-selling-plans')?.shadowRoot);
            });
        };

        const POLL_INTERVAL = 200;
        const POLL_TIMEOUT = 30000;
        let pollId = null;
        let pollElapsed = 0;

        const isPending = () =>
            [...document.querySelectorAll('recharge-subscription-widget')].some(
                (widget) => !widget.shadowRoot?.querySelector('.rc-purchase-option')
            );

        const startPolling = () => {
            if (pollId) return;

            pollElapsed = 0;
            pollId = setInterval(() => {
                pollElapsed += POLL_INTERVAL;
                applyAll();

                if (!isPending() || pollElapsed >= POLL_TIMEOUT) {
                    clearInterval(pollId);
                    pollId = null;
                }
            }, POLL_INTERVAL);
        };

        const start = () => {
            applyAll();
            startPolling();

            customElements.whenDefined('recharge-subscription-widget').then(applyAll);

            new MutationObserver(() => {
                applyAll();
                if (isPending()) startPolling();
            }).observe(document.body, {
                childList: true,
                subtree: true
            });
        };

        if (document.body) {
            start();
        } else {
            document.addEventListener('DOMContentLoaded', start, { once: true });
        }
    } catch (error) {
        console.error('Error initializing PDP Recharge subscription widget:', error);
    }
})();
